import { StatsReport } from "./stats";

/**
 * TypeScript interfaces for Equipment Orders Sunburst Chart
 * Represents the hierarchical structure of machine equipment data
 */

export interface EquipmentOrderReport extends StatsReport {
    reportData: EquipmentOrderData[]
}

export interface EquipmentOrderData {
  MACHINETYPE: string;
  MACHINEPRODUCT: string;
  MACHINEMARKET: string;
  MACHINEVARIANT: string;
  MACHINESIZE: string;
  MANUFACTUREDATE: string;
  ManufactureYear: number;
  ManufactureQuarter: number;
  YearQuarter: string;
  Region: string;
  Country: string;
  Company_Name: string;
  Plant_ID: string;
  CONTRACT?: string;
  CONTRACTEND?: string;
  COMPANYID: string;
  PLANTNAME: string;
  COUNTRYCODE: string;
  OrderCount: number;
}

export interface SunburstNode {
  name: string;
  value?: number;
  children?: SunburstNode[];
  depth: number;
  x0: number;
  x1: number;
  y0: number;
  y1: number;
  parent?: SunburstNode;
  data?: EquipmentOrderData[];
  // D3 specific properties
  startAngle?: number;
  endAngle?: number;
  innerRadius?: number;
  outerRadius?: number;
  color?: string;
}

export interface HierarchyLevel {
  key: keyof EquipmentOrderData;
  label: string;
  depth: number;
}

export interface SunburstChartProps {
  width?: number;
  height?: number;
  className?: string;
  onNodeClick?: (node: SunburstNode) => void;
  onNodeHover?: (node: SunburstNode | null) => void;
  colorScheme?: string[];
  innerRadius?: number;
  outerRadius?: number;
}

export interface TimeFilterState {
  startYear: number;
  endYear: number;
  currentYear: number;
  isPlaying: boolean;
  playbackSpeed: number;
}

export interface PlantFilterState {
  selectedRegions: string[];
  selectedCountries: string[];
  selectedCompanies: string[];
  selectedPlants: string[];
}

export interface SunburstData {
  hierarchyLevels: HierarchyLevel[];
  root: SunburstNode;
  metadata: {
    totalOrders: number;
    dateRange: {
      min: string;
      max: string;
    };
    yearRange: {
      min: number;
      max: number;
    };
    regions: string[];
    countries: string[];
    companies: string[];
  };
}

export interface SunburstTooltipData {
  node: SunburstNode;
  percentage: number;
  path: string[];
  orderCount: number;
  level: string;
}

export interface ChronologicalSliderProps {
  yearRange: { min: number; max: number };
  currentYear: number;
  isPlaying: boolean;
  playbackSpeed: number;
  onYearChange: (year: number) => void;
  onPlayToggle: () => void;
  onSpeedChange: (speed: number) => void;
  className?: string;
}

export interface SunburstLegendProps {
  hierarchyLevels: HierarchyLevel[];
  colorSchemes: { [key: string]: string[] };
  className?: string;
}

// Constants for the sunburst hierarchy
export const HIERARCHY_LEVELS: HierarchyLevel[] = [
  { key: 'MACHINEPRODUCT', label: 'Product', depth: 0 },
  { key: 'MACHINEMARKET', label: 'Market', depth: 1 },
  { key: 'MACHINEVARIANT', label: 'Variant', depth: 2 }
];

export const DEFAULT_COLOR_SCHEME = [
  '#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd',
  '#8c564b', '#e377c2', '#7f7f7f', '#bcbd22', '#17becf',
  '#aec7e8', '#ffbb78', '#98df8a', '#ff9896', '#c5b0d5',
  '#c49c94', '#f7b6d3', '#c7c7c7', '#dbdb8d', '#9edae5'
];

// Hierarchical color schemes with different saturation/brightness levels for better contrast
export const HIERARCHICAL_COLOR_SCHEMES = {
  // Level 1 (MACHINEPRODUCT) - Vibrant colors
  level1: [
    '#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd',
    '#8c564b', '#e377c2', '#17becf', '#bcbd22', '#e8c547'
  ],
  // Level 2 (MACHINEMARKET) - Medium saturation colors
  level2: [
    '#4c94c4', '#ff9f3e', '#4cb04c', '#e04748', '#b487d5',
    '#ac766b', '#f797e2', '#37cedf', '#dce542', '#f8d567'
  ],
  // Level 3 (MACHINEVARIANT) - Lighter colors
  level3: [
    '#7fb1d4', '#ffbf6e', '#6cc06c', '#e86768', '#c4a7e5',
    '#cc968b', '#fbb7f2', '#57deef', '#ece562', '#fde587'
  ],
  // Level 4 (Individual items) - Very light colors
  level4: [
    '#aec7e8', '#ffdf9e', '#98df8a', '#ff9896', '#d4c7f5',
    '#dbb6ab', '#fdd7ff', '#87eeff', '#fcf582', '#ffe5a7'
  ]
};